'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Commitment {
  icon: string;
  text: string;
}

interface CTAContent {
  icon: string;
  headline: string;
  description: string;
  primaryCTA: { text: string; link: string };
  secondaryCTA: { text: string; link: string };
  commitments: Commitment[];
  trustBadges: string[];
}

interface CallToActionProps {
  className?: string;
}

const CallToAction = ({ className = '' }: CallToActionProps) => {
  const [content, setContent] = useState<CTAContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('content_pages')
        .select('content')
        .eq('slug', 'homepage_cta')
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;
      
      if (data?.content) {
        const parsedContent = typeof data.content === 'string' 
          ? JSON.parse(data.content) 
          : data.content;
        setContent(parsedContent);
      }
    } catch (error) {
      console.error('Error loading CTA content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !content) {
    return (
      <section className={`bg-surface py-20 lg:py-28 ${className}`}>
        <div className="mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse bg-muted rounded h-96"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`bg-surface py-20 lg:py-28 ${className}`}>
      <div className="mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-2 border-accent rounded-sm p-12 text-center shadow-institutional">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name={content.icon as any} size={32} variant="solid" className="text-accent" />
            </div>

            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              {content.headline}
            </h2>

            <p className="font-body text-lg text-text-secondary mb-8 leading-relaxed max-w-2xl mx-auto">
              {content.description}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
              <Link
                href={content.primaryCTA.link}
                className="w-full sm:w-auto px-8 py-4 bg-cta text-cta-foreground font-headline font-semibold text-base rounded-sm hover:bg-accent transition-colors duration-smooth shadow-institutional"
              >
                {content.primaryCTA.text}
              </Link>
              <Link
                href={content.secondaryCTA.link}
                className="w-full sm:w-auto px-8 py-4 bg-white text-primary border-2 border-primary font-headline font-semibold text-base rounded-sm hover:bg-surface transition-colors duration-smooth"
              >
                {content.secondaryCTA.text}
              </Link>
            </div>

            <div className="pt-8 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {content.commitments.map((commitment, index) => (
                  <div key={index} className="flex items-center justify-center space-x-3">
                    <Icon name={commitment.icon as any} size={20} variant="solid" className="text-success" />
                    <span className="font-body text-sm text-text-secondary">
                      {commitment.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="font-accent text-sm text-text-secondary mb-4">
              Trusted by Marketing Agency Principals and Professional Media Buyers
            </p>
            <div className="flex items-center justify-center space-x-6">
              {content.trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
                  <span className="font-body text-sm text-text-primary">
                    {badge}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;